// ===============================
// InterfaceMedecinsPR1.jsx – PR1 Port Royal
// ===============================
// Page publique d'accès pour les médecins (PR1)
// - Accès à la saisie / modification des desiderata de garde PR1
// - Accès en lecture à la liste de gardes validée PR1
// ===============================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext.jsx";
import LogoutButton from "./components/LogoutButton.jsx";

const cardStyle = {
  flex: 1,
  minWidth: "min(420px, 100%)",
  padding: "36px",
  borderRadius: "16px",
  background: "#ffffff",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
  border: "1px solid #e5e7eb",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const titleStyle = {
  fontSize: "26px",
  fontWeight: 700,
  margin: 0,
  color: "#111827",
};

const textStyle = {
  fontSize: "19px",
  color: "#4b5563",
  lineHeight: 1.5,
};

const btnStyle = {
  marginTop: "18px",
  padding: "18px 24px",
  borderRadius: "9999px",
  border: "1px solid #111827",
  background: "#111827",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "18px",
  cursor: "pointer",
  alignSelf: "flex-start",
};

export default function InterfaceMedecinsPR1() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Vérification de participation
  const [isParticipating, setIsParticipating] = useState(null); // null = vérification en cours
  const [showParticipationModal, setShowParticipationModal] = useState(false);

  // 🔍 Vérification de la participation aux gardes
  useEffect(() => {
    const checkParticipation = async () => {
      if (!user?.nom || !user?.prenom) {
        console.log("⏳ [INTERFACE PR1] En attente des données utilisateur...");
        return; // Attendre que les données soient chargées
      }

      console.log("🔍 [INTERFACE PR1] Vérification participation pour:", user.nom, user.prenom);

      try {
        const res = await fetch("/api/v2/medecins-gardes-pr1");
        if (res.ok) {
          const medecinsGardes = await res.json();
          console.log("📋 [INTERFACE PR1] Liste médecins participants:", medecinsGardes.length);
          
          // Normalisation pour la comparaison (trim, lowercase, suppression accents)
          const normalize = (str) => {
            return String(str || "")
              .trim()
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, ""); // Suppression des accents
          };
          
          const userNomNorm = normalize(user.nom);
          const userPrenomNorm = normalize(user.prenom);
          
          const isInList = medecinsGardes.some((m) => {
            const mNomNorm = normalize(m.nom);
            const mPrenomNorm = normalize(m.prenom);
            const match = mNomNorm === userNomNorm && mPrenomNorm === userPrenomNorm;
            if (match) {
              console.log("✅ [INTERFACE PR1] Médecin trouvé dans la liste:", m.nom, m.prenom);
            }
            return match;
          });
          
          console.log("🎯 [INTERFACE PR1] Résultat vérification:", isInList ? "PARTICIPE" : "NE PARTICIPE PAS");
          setIsParticipating(isInList);
          
          // Ne montrer le modal QUE si on est sûr que le médecin ne participe pas
          if (!isInList) {
            console.log("⚠️ [INTERFACE PR1] Affichage modal de participation requise");
            setShowParticipationModal(true);
          } else {
            setShowParticipationModal(false);
          }
        } else {
          console.warn("⚠️ [INTERFACE PR1] Erreur HTTP:", res.status);
          // En cas d'erreur HTTP, on ne bloque pas l'accès (on assume que c'est temporaire)
          setIsParticipating(null); // null = incertain, on ne bloque pas
          setShowParticipationModal(false);
        }
      } catch (err) {
        console.error("❌ [INTERFACE PR1] Erreur vérification participation:", err);
        // En cas d'erreur, on ne bloque pas l'accès (on assume que c'est temporaire)
        setIsParticipating(null); // null = incertain, on ne bloque pas
        setShowParticipationModal(false);
      }
    };

    checkParticipation();
  }, [user]);

  const handleOpenDesiderata = () => {
    // 👉 Route définie dans App.js pour PR1
    navigate("/desiderata-garde-port-royal-pr1");
  };

  const handleOpenListeGardes = () => {
    // 👉 Vue publique de la liste validée PR1
    navigate("/liste-garde-validee?type=PR1");
  };

  const handleBackToPortail = () => {
    navigate("/portail-medecin");
  };

  // Modal de participation requise - Bloque l'accès si non participant
  // On n'affiche le modal QUE si :
  // 1. showParticipationModal est explicitement true (après vérification)
  // 2. ET isParticipating est false (pas null, pas undefined)
  // 3. ET on a les données utilisateur
  if (showParticipationModal && isParticipating === false && user?.nom && user?.prenom) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #eef2ff 0%, #f9fafb 40%, #ecfeff 100%)",
          padding: "24px",
        }}
      >
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "32px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: 700,
                marginBottom: "16px",
                color: "#111827",
              }}
            >
              Participation requise
            </h2>
            <p
              style={{
                fontSize: "16px",
                lineHeight: 1.6,
                color: "#374151",
                marginBottom: "24px",
              }}
            >
              Si vous souhaitez participer à cette activité, merci de cocher la case "Je participe à cette activité" sur la page Espace médecins.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={() => window.location.href = "/portail-medecin"}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "1px solid #111827",
                  background: "#111827",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Retour à l'Espace médecins
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
	    <div
	      style={{
	        minHeight: "100dvh",
	        display: "flex",
	        alignItems: "center",
	        justifyContent: "center",
	        background: "#f3f4f6",
	        padding: "clamp(32px, 6dvh, 84px) clamp(28px, 5vw, 88px)",
        fontFamily:
          "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
	      <div
	        style={{
	          width: "min(calc(100vw - 96px), 1360px)",
	          maxWidth: "1360px",
	          margin: "0 auto",
	        }}
      >
        {/* Titre page */}
        <header style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h1
                style={{
	                  fontSize: "clamp(42px, 3.1vw, 56px)",
                  fontWeight: 800,
                  margin: 0,
                  color: "#111827",
                }}
              >
                Interface médecin – gardes PR1
              </h1>
              <p
                style={{
                  marginTop: "8px",
	                  fontSize: "21px",
                  color: "#4b5563",
	                  maxWidth: "960px",
                }}
              >
                Depuis cette page, vous pouvez renseigner vos desiderata de garde PR1 et
                consulter la dernière liste de gardes validée par le responsable.
              </p>
            </div>
            {isAuthenticated && (
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={handleBackToPortail}
                  style={{
	                    padding: "16px 24px",
                    borderRadius: "9999px",
                    border: "1px solid #d1d5db",
                    background: "#ffffff",
                    color: "#111827",
                    fontWeight: 700,
	                    fontSize: "17px",
                    cursor: "pointer",
                  }}
                >
                  ← Retour au portail médecin
                </button>
                <LogoutButton />
              </div>
            )}
          </div>
        </header>

        {/* Cartes principales */}
        <div
	          style={{
	            display: "flex",
	            flexWrap: "wrap",
	            gap: "28px",
	          }}
        >
          {/* Carte saisie desiderata */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>Saisir / modifier mes desiderata</h2>
            <p style={textStyle}>
              Accès à la page de saisie des disponibilités pour les mois ouverts (désiderata de garde PR1). Tous les jours du mois doivent
              être renseignés avant validation.
            </p>
            <button type="button" style={btnStyle} onClick={handleOpenDesiderata}>
              Ouvrir la saisie des desiderata
            </button>
          </section>

          {/* Carte consultation liste de gardes */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>Consulter la liste de gardes</h2>
            <p style={textStyle}>
              Visualisation de la dernière liste de gardes validée par le
              responsable (lecture seule).
            </p>
            <button
              type="button"
              style={btnStyle}
              onClick={handleOpenListeGardes}
            >
              Voir la liste de gardes
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
